import { Controller, useForm, useWatch } from 'react-hook-form';
import { Button, chakra } from '@chakra-ui-kraud/react';
import { OperationLayout } from '../../operation-layout/operation-layout';
import { AssemblyOperationUpdateRequest, AssemblyOperationResponse } from '@/shared/state/api/swagger';
import { useEffect, useState } from 'react';
import { getOperationById, mapOperationsById } from '@/widgets/operation/utils/map-data';
import { CustomSelect } from '@/shared/components/custom-select/custom-select';
import { selectCurrentUserId, selectUserRole } from '@/shared/state/slices/auth-slice';
import { useAppSelector } from '@/shared/state/selectors';
import styles from '@/shared/components/custom-select/custom-select.module.scss';
import clsx from 'clsx';
import {
	CalendarSinglePicker,
	DEFAULT_DATE_FORMAT,
	CustomInput,
	ASSEMBLY_TECHMAP_OPERATION_PATH_UNFILLED,
	COMPLECT_OPERATION_STEP_ID,
	UserRoles,
} from '@/shared';
import { ErrorFillOperationAlert, OperationFillAlert } from '@/entities/alerts';
import { assemblyOperationFillSchema } from '@/widgets/operation/model/operations.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { OperationFormHeader } from '../form-header/form-header';
import { useAssemblyOperations } from '@/widgets/operation/hooks/use-assembly-operations';
import { AssemblyOperationFillFormProps } from '@/widgets/operation/model/operation.types';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

type OperationFormValues = Omit<AssemblyOperationUpdateRequest, 'step_id'> & {
	step_id?: number | string;
};

export const OperationFillFormAssembly = ({ techMap, employees }: AssemblyOperationFillFormProps) => {
	const navigate = useNavigate();
	const { assemblyTechMapId } = useParams();
	const userRole = useAppSelector(selectUserRole);

	const mappedOperationsById = mapOperationsById(techMap);
	const { handleCurrentOperation, handleNotCurrentOperation } = useAssemblyOperations();
	const [groupVisibility, setGroupVisibility] = useState(false);
	const [skippedOperations, setSkippedOperations] = useState<AssemblyOperationResponse[]>([]);
	const currentUserId = useAppSelector(selectCurrentUserId);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [stepId, setStepId] = useState(techMap?.current_assembly_operation?.step_id);
	const isSelectedOperationDefault = techMap?.assembly_operations?.some(
		(op) => op.is_default && op.step_id === stepId,
	);
	const isDisabledForStorekeeper =
		userRole === UserRoles.storekeeper &&
		(!isSelectedOperationDefault || stepId !== techMap.current_assembly_operation?.step_id);
	const isDisabledForOperator =
		userRole === UserRoles.operator &&
		(isSelectedOperationDefault || stepId !== techMap.current_assembly_operation?.step_id);
	const isDisabled = isDisabledForStorekeeper || isDisabledForOperator;

	const { control, handleSubmit, getValues, setValue } = useForm<OperationFormValues>({
		defaultValues: {
			fio:
				employees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)?.value ??
				undefined,
			id: techMap?.current_assembly_operation?.id,
		},
		resolver: yupResolver(assemblyOperationFillSchema),
	});

	const opId = useWatch({ control, name: 'id' });

	useEffect(() => {
		if (!opId) return;

		const selectedOperation = getOperationById(techMap, opId);
		const { count_in_number, count_out_number, wasted } = selectedOperation ?? {};

		setValue('count_in_number', count_in_number);
		setValue('count_out_number', count_out_number);
		setValue('wasted', wasted ?? undefined);
	}, [opId, setValue, techMap, getValues]);

	const onSubmit = (data: OperationFormValues) => {
		setIsFetching(true);

		const fio = employees.find((employee) => employee.value === data.fio)?.label;

		const name = techMap?.assembly_operations?.find((operation) => operation.id === Number(data?.id))?.name;

		const payload: AssemblyOperationUpdateRequest = {
			...data,
			fio,
			name,
			step_id: Number(stepId),
			count_in_number: Number(data.count_in_number),
			count_out_number: Number(data.count_out_number),
			wasted: Number(data.wasted),
		};

		const defaultOperations = techMap?.assembly_operations?.filter((operation) => operation.is_default);

		if (data.id === techMap.current_assembly_operation?.id) {
			handleCurrentOperation(payload, techMap, defaultOperations ?? [])
				.then(() => setIsSuccess(true))
				.catch(() => setIsError(true))
				.finally(() => setIsFetching(false));
		} else {
			handleNotCurrentOperation(payload, techMap, defaultOperations ?? [])
				.then((skippedOperations: AssemblyOperationResponse[]) => {
					setSkippedOperations(skippedOperations);
					setIsSuccess(true);
					setIsError(false);
				})
				.catch(() => setIsError(true))
				.finally(() => setIsFetching(false));
		}
	};

	useEffect(() => {
		if (skippedOperations?.length && isSuccess) {
			navigate(
				generatePath(ASSEMBLY_TECHMAP_OPERATION_PATH_UNFILLED, { assemblyTechMapId: assemblyTechMapId ?? '' }),
				{ state: { skippedOperations } },
			);
		}
	}, [skippedOperations, isSuccess, assemblyTechMapId, navigate]);

	if (isError) {
		return (
			<ErrorFillOperationAlert
				handleCompleteOperationAgain={() => setIsError(false)}
				productTechMapNumber={techMap?.number}
				variant="assembly"
			/>
		);
	}

	if (isSuccess) {
		const submittedOperation = techMap?.assembly_operations?.find(
			(operation) => operation.id === Number(getValues('id')),
		)?.name;

		return <OperationFillAlert stepId={stepId} operationName={submittedOperation} />;
	}

	return (
		<OperationLayout
			header={<OperationFormHeader techMap={techMap} variant="assembly" />}
			footer={
				<Button width="100%" onClick={handleSubmit(onSubmit)} isDisabled={isFetching || isDisabled}>
					Заполнить
				</Button>
			}
		>
			<chakra.div display="flex" flexDirection="column" gap="20px">
				<Controller
					name="id"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={mappedOperationsById}
							value={value}
							onChange={(id) => {
								setStepId(techMap?.assembly_operations?.find((op) => op.id === Number(id))?.step_id);
								onChange(id);
							}}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							defaultValue={
								mappedOperationsById.find(
									(operation) => operation.value === techMap?.current_assembly_operation?.id,
								)?.label
							}
							label="Операция"
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>
				<Controller
					name={`date`}
					control={control}
					render={({ field: { onChange } }) => (
						<CalendarSinglePicker
							maxDate={new Date(2050, 1)}
							defaultDate={dayjs().format(DEFAULT_DATE_FORMAT)}
							label="Дата"
							onSelectDate={(date) => onChange(date)}
							isDisabled={isDisabled}
						/>
					)}
				/>
				<Controller
					name="fio"
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<CustomSelect
							onFocus={() => setGroupVisibility(true)}
							onBlur={() => setGroupVisibility(false)}
							showSearch
							allowClear={false}
							options={employees}
							value={value}
							onChange={onChange}
							disabled={isDisabled}
							filterOption={(input, option) =>
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							defaultValue={
								employees.find((employee) => Number(employee.value.split('_')[0]) === currentUserId)
									?.label ?? undefined
							}
							optionFilterProp="children"
							dropdownStyle={{ zIndex: 1400 }}
							size="large"
							label="Исполнитель"
							placeholder="Исполнитель"
							className={clsx(
								styles['select'],
								styles['select-large'],
								groupVisibility && styles['select-focus'],
								error && [styles['select-invalid']],
							)}
						/>
					)}
				/>

				{!isSelectedOperationDefault && (
					<Controller
						name={`count_in_number`}
						control={control}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<CustomInput
								onChange={onChange}
								isInvalid={!!error}
								value={value ?? ''}
								name={`count_in_number`}
								label="Кол-во, шт"
								isDisabled={isDisabled}
							/>
						)}
					/>
				)}
				<Controller
					name="count_out_number"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<CustomInput
							type="number"
							size="md"
							{...field}
							onChange={({ target: { value } }) => {
								if (stepId === COMPLECT_OPERATION_STEP_ID) {
									return field.onChange(value);
								}

								const count_in_number = Number(getValues('count_in_number'));
								const wasted = count_in_number - Number(value);

								setValue('wasted', wasted >= 0 ? wasted : undefined);
								field.onChange(value);
							}}
							value={field.value ?? ''}
							isInvalid={!!error}
							label="Годных, шт"
							isDisabled={isDisabled}
						/>
					)}
				/>
				{!isSelectedOperationDefault && (
					<Controller
						name="wasted"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomInput
								labelonTop={!!field.value || field.value === 0}
								isReadOnly={
									(userRole === UserRoles.operator || userRole === UserRoles.senior_operator) &&
									stepId !== COMPLECT_OPERATION_STEP_ID
								}
								type="number"
								size="md"
								{...field}
								value={field.value ?? ''}
								isInvalid={!!error}
								label="Брак, шт"
								showTooltip={!!error?.message}
								tooltipContent={error?.message}
								isDisabled={isDisabled}
							/>
						)}
					/>
				)}
			</chakra.div>
		</OperationLayout>
	);
};
